import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LogOut, Menu, UserRound, Settings as SettingsIcon, LifeBuoy, ChevronDown } from "lucide-react";

function useNavLinks(user) {
  const isProvider = user && user.role === "subscriber";
  const links = [];
  // Providers don't browse the customer-facing marketplace, so hide Browse + Packages for them.
  if (!isProvider) {
    links.push({ to: "/browse", label: "Browse", testid: "nav-browse" });
    links.push({ to: "/packages", label: "Packages", testid: "nav-packages" });
  }
  // Pricing is about provider subscription tiers — irrelevant to customers.
  if (!user || user.role !== "customer") links.push({ to: "/pricing", label: "Pricing", testid: "nav-pricing" });
  if (user && user !== false) {
    if (user.role === "admin") links.push({ to: "/admin", label: "Admin", testid: "nav-admin" });
    if (user.role === "subscriber") links.push({ to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" });
    if (user.role === "customer") {
      links.push({ to: "/home", label: "Dashboard", testid: "nav-dashboard" });
      if (user.customer_type === "business") links.push({ to: "/business", label: "Business", testid: "nav-business" });
    }
  }
  return links;
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = useNavLinks(user);
  const authed = user && user !== false;
  const showSupport = authed && user.role !== "admin";

  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <img src="/logo-mark.png" alt="BookdHUB" className="w-8 h-8 rounded-sm" />
          <span className="font-display text-lg sm:text-xl tracking-tight">
            <span className="font-semibold text-foreground">Bookd</span><span className="font-bold text-primary">HUB</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          {links.map((l) => (
            <Link key={l.to} to={l.to}><Button variant="ghost" size="sm" data-testid={l.testid}>{l.label}</Button></Link>
          ))}
          {authed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="nav-user-menu">
                  <UserRound className="w-4 h-4" />
                  <span className="max-w-[120px] truncate">{(user.name || "Account").split(" ")[0]}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-profile"><Link to="/profile"><UserRound className="w-4 h-4 mr-2" />Profile</Link></DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-settings"><Link to="/settings"><SettingsIcon className="w-4 h-4 mr-2" />Settings</Link></DropdownMenuItem>
                {showSupport && <DropdownMenuItem asChild data-testid="menu-support"><Link to="/support"><LifeBuoy className="w-4 h-4 mr-2" />Support</Link></DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="nav-logout"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login">Login</Button></Link>
              <Link to="/register"><Button size="sm" className="rounded-sm" data-testid="nav-signup">Get Started</Button></Link>
            </>
          )}
        </nav>

        {/* Mobile: signup CTA (guests) + hamburger */}
        <div className="flex items-center gap-2 lg:hidden">
          {!authed && (
            <Link to="/register"><Button size="sm" className="rounded-sm" data-testid="nav-signup-mobile">Get Started</Button></Link>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-sm px-2.5" data-testid="nav-menu-toggle" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[min(20rem,85vw)] flex flex-col p-6">
              <SheetTitle className="font-display text-xl">Menu</SheetTitle>
              {authed && <p className="text-sm text-muted-foreground -mt-2 truncate">Signed in as {user.name}</p>}
              <nav className="flex flex-col gap-1 mt-4">
                {links.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <Link to={l.to} className="w-full">
                      <Button variant="ghost" className="w-full justify-start rounded-sm" data-testid={`${l.testid}-mobile`}>{l.label}</Button>
                    </Link>
                  </SheetClose>
                ))}
                {authed && (
                  <div className="flex flex-col gap-1 mt-1 pt-3 border-t border-border">
                    <SheetClose asChild>
                      <Link to="/profile" className="w-full"><Button variant="ghost" className="w-full justify-start rounded-sm" data-testid="menu-profile-mobile"><UserRound className="w-4 h-4 mr-2" />Profile</Button></Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/settings" className="w-full"><Button variant="ghost" className="w-full justify-start rounded-sm" data-testid="menu-settings-mobile"><SettingsIcon className="w-4 h-4 mr-2" />Settings</Button></Link>
                    </SheetClose>
                    {showSupport && (
                      <SheetClose asChild>
                        <Link to="/support" className="w-full"><Button variant="ghost" className="w-full justify-start rounded-sm" data-testid="menu-support-mobile"><LifeBuoy className="w-4 h-4 mr-2" />Support</Button></Link>
                      </SheetClose>
                    )}
                  </div>
                )}
              </nav>
              <div className="mt-auto pt-4 border-t border-border">
                {authed ? (
                  <Button variant="outline" className="w-full rounded-sm" onClick={onLogout} data-testid="nav-logout-mobile">
                    <LogOut className="w-4 h-4 mr-2" />Log out
                  </Button>
                ) : (
                  <div className="flex flex-col gap-2">
                    <SheetClose asChild>
                      <Link to="/login" className="w-full"><Button variant="outline" className="w-full rounded-sm" data-testid="nav-login-mobile">Login</Button></Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/register" className="w-full"><Button className="w-full rounded-sm" data-testid="nav-signup-drawer">Get Started</Button></Link>
                    </SheetClose>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {authed && user.email_verified === false && (
        <div className="bg-amber-50 border-t border-amber-200 text-amber-900 text-sm px-4 py-2 text-center" data-testid="verify-email-banner">
          Please verify your email to book and pay for services.{" "}
          <Link to={`/verify-email?email=${encodeURIComponent(user.email)}`} className="font-semibold underline" data-testid="verify-email-banner-link">Verify now</Link>
        </div>
      )}
    </header>
  );
}
